import GuideLayout, {
  H2,
  H3,
  P,
  UL,
  OL,
  Callout,
  ExternalLink,
  Strong,
  Em,
} from "../ui/GuideLayout.jsx";
import { Link } from "react-router-dom";

export default function QuantoGanhaOperario() {
  return (
    <GuideLayout
      title="Quanto ganha um operário no Japão em 2026"
      subtitle="A pergunta direta, sem rodeio: salários reais por hora, valor médio mensal, diferenças por região e por gênero, e onde o número que circula no WhatsApp está errado."
      slug="quanto-ganha-operario-japao-2026"
      description="Salário real de operário de fábrica no Japão em 2026. Valor por hora, média mensal, salário mínimo, variação por região e tipo de contrato."
      updated="Maio de 2026"
      readTime="12 min"
    >
      <P>
        Todo brasileiro que pensa em vir trabalhar no Japão faz a mesma
        pesquisa: <Em>&ldquo;quanto ganha um operário no Japão?&rdquo;</Em>. As
        respostas que aparecem no Google são quase todas erradas — ou
        defasadas, ou de empreiteira querendo te vender uma vaga, ou de blogs
        que copiaram um número de 2018 e nunca atualizaram.
      </P>
      <P>
        Este guia traz os números reais de 2026, baseados em dados do
        Ministério do Trabalho japonês e na média do que os brasileiros de
        fábrica estão de fato recebendo. Sem inflar, sem subestimar.
      </P>

      <H2 id="resposta-rapida">Resposta rápida</H2>
      <P>
        Em 2026, um operário brasileiro recente em fábrica japonesa
        (contrato via empreiteira, sem qualificação técnica) ganha em média:
      </P>
      <UL>
        <li>
          <Strong>Por hora:</Strong> ¥1.200 a ¥1.500 (homens), ¥1.050 a ¥1.300 (mulheres)
        </li>
        <li>
          <Strong>Por mês (40h/semana, sem hora extra):</Strong> ¥180.000 a ¥240.000 bruto
        </li>
        <li>
          <Strong>Por mês (com hora extra típica):</Strong> ¥250.000 a ¥320.000 bruto
        </li>
        <li>
          <Strong>Líquido após shakai hoken + imposto:</Strong> ¥200.000 a ¥260.000 mensal
        </li>
      </UL>
      <Callout>
        <Strong>Importante:</Strong> esses valores são para <Em>contrato via empreiteira</Em>{" "}
        (派遣 / hakken). Quem é seishain (正社員 — funcionário fixo da empresa)
        geralmente ganha 10% a 30% a mais e tem benefícios maiores.
      </Callout>

      <H2 id="salario-minimo">Salário mínimo no Japão em 2026</H2>
      <P>
        O salário mínimo é definido por <Strong>prefeitura</Strong>, não por
        lei nacional única. Em 2026 a média nacional ficou em{" "}
        <Strong>¥1.054 por hora</Strong>. Mas a variação entre regiões é
        grande:
      </P>
      <UL>
        <li>
          <Strong>Tóquio:</Strong> ¥1.163/hora — o mais alto do país
        </li>
        <li>
          <Strong>Kanagawa, Osaka:</Strong> ¥1.150–1.160/hora
        </li>
        <li>
          <Strong>Aichi (Nagoya, Toyota):</Strong> ¥1.140/hora — onde mora grande parte dos brasileiros
        </li>
        <li>
          <Strong>Shizuoka (Hamamatsu):</Strong> ¥1.100/hora
        </li>
        <li>
          <Strong>Mie (Suzuka):</Strong> ¥1.075/hora
        </li>
        <li>
          <Strong>Gunma (Ota, Oizumi):</Strong> ¥1.050/hora
        </li>
        <li>
          <Strong>Okinawa, Tohoku rural:</Strong> ¥950–980/hora — o mais baixo
        </li>
      </UL>
      <P>
        Quase ninguém ganha exatamente o mínimo. As fábricas pagam acima para
        atrair gente, especialmente para turnos noturnos ou trabalhos sujos
        (3K: <Em>kitanai, kitsui, kiken</Em> — sujo, pesado, perigoso).
      </P>

      <H2 id="como-funciona-pagamento">Como o pagamento realmente funciona</H2>
      <P>
        Quase toda fábrica que contrata brasileiro paga <Strong>por hora</Strong>,
        não por mês. Você bate ponto na entrada e na saída, e no fim do mês
        recebe pelo total de horas trabalhadas. Isso muda totalmente a conta.
      </P>

      <H3>O básico</H3>
      <P>
        Imagine que o valor da sua hora é <Strong>¥1.300</Strong>. Trabalhando
        8 horas por dia, 22 dias úteis no mês:
      </P>
      <UL>
        <li>8 × 22 = 176 horas mensais</li>
        <li>176 × ¥1.300 = <Strong>¥228.800 bruto</Strong></li>
      </UL>

      <H3>Hora extra (zangyo / 残業)</H3>
      <P>
        Por lei japonesa, hora extra paga <Strong>25% a mais</Strong> que a
        hora normal. Então naquele exemplo:
      </P>
      <UL>
        <li>Hora normal: ¥1.300</li>
        <li>Hora extra: ¥1.625</li>
      </UL>
      <P>
        Se você fizer 2 horas extras por dia, 22 dias:
      </P>
      <UL>
        <li>44 horas extras × ¥1.625 = <Strong>¥71.500 adicional</Strong></li>
      </UL>
      <P>
        Com esse padrão, você passa de ¥228.800 para <Strong>¥300.300/mês bruto</Strong>.
      </P>

      <H3>Turno noturno (yakin / 夜勤)</H3>
      <P>
        Trabalho entre <Strong>22h e 5h</Strong> paga +25%. Algumas fábricas
        têm turno fixo noturno e os brasileiros preferem porque rende mais.
        Hora noturna típica: ¥1.625 a ¥1.875 dependendo da hora base.
      </P>

      <H3>Hora extra noturna</H3>
      <P>
        Se você faz hora extra durante turno noturno, os 25% se acumulam:
        +25% (extra) +25% (noturno) = +50% sobre a hora base. Hora extra
        noturna a ¥1.950+ é comum em fábrica que paga ¥1.300 a base.
      </P>

      <H3>Final de semana e feriado</H3>
      <P>
        Trabalho em <Strong>feriado nacional</Strong> ou no <Strong>sétimo dia consecutivo</Strong>{" "}
        paga +35%. Domingo NÃO é automaticamente +25% — só conta como extra
        se você já trabalhou os outros 6 dias na semana.
      </P>

      <H2 id="diferenca-bruto-liquido">O bruto não é o que chega na conta</H2>
      <P>
        Aqui é onde muita gente se surpreende. Um salário de <Strong>¥300.000 bruto</Strong>{" "}
        chega na conta como <Strong>¥235.000 a ¥250.000</Strong>. Os descontos:
      </P>
      <UL>
        <li>
          <Strong>Shakai hoken (社会保険):</Strong> ~15% — saúde + aposentadoria + seguro-desemprego
        </li>
        <li>
          <Strong>Imposto de renda (所得税):</Strong> ~3% a 5% conforme o salário
        </li>
        <li>
          <Strong>Imposto residencial (住民税):</Strong> ~5% a 7% — só a partir do segundo ano (o primeiro é ¥0)
        </li>
      </UL>
      <P>
        Para ver o número real do seu caso, use a{" "}
        <Link to="/calculadoras/salario" className="text-accent underline">
          calculadora de salário líquido
        </Link>
        . Ela considera idade, dependentes, prefeitura e se é primeiro ano fiscal.
      </P>

      <H2 id="comparacao-regiao">Salário por região onde brasileiros trabalham</H2>
      <P>
        Estas são as cidades com maior concentração de brasileiros e o
        salário médio de fábrica em cada uma (homem operador típico, sem
        especialização, com hora extra moderada):
      </P>
      <UL>
        <li>
          <Strong>Toyota / Anjo / Nagoya (Aichi):</Strong> ¥1.350–1.500/hora — fábricas de carro e autopeças
        </li>
        <li>
          <Strong>Hamamatsu / Iwata (Shizuoka):</Strong> ¥1.250–1.400/hora — Suzuki, Yamaha, Honda
        </li>
        <li>
          <Strong>Suzuka / Yokkaichi (Mie):</Strong> ¥1.250–1.400/hora — Honda, química, alimentícia
        </li>
        <li>
          <Strong>Ota / Oizumi (Gunma):</Strong> ¥1.200–1.350/hora — Subaru, peças
        </li>
        <li>
          <Strong>Hirakata / Maizuru (Kyoto/Osaka):</Strong> ¥1.250–1.400/hora — Kubota, Panasonic, autopeças
        </li>
        <li>
          <Strong>Toyohashi / Kosai (Aichi/Shizuoka):</Strong> ¥1.200–1.350/hora — Toyota indireto
        </li>
        <li>
          <Strong>Áreas rurais (Tohoku, Kyushu):</Strong> ¥1.000–1.150/hora — fábrica menor, alimentos
        </li>
      </UL>

      <H2 id="diferenca-genero">A diferença salarial entre homens e mulheres</H2>
      <P>
        Existe e é grande. Em fábrica brasileira no Japão, mulheres
        tipicamente ganham <Strong>¥150 a ¥300 a menos por hora</Strong> que
        homens na mesma função.
      </P>
      <P>
        A justificativa formal das empreiteiras é &ldquo;diferença de força
        física&rdquo;, mas na prática:
      </P>
      <UL>
        <li>
          Mulheres são alocadas em montagem fina, inspeção, embalagem — funções pagas como &ldquo;mais leves&rdquo;
        </li>
        <li>
          Homens são alocados em estamparia, soldagem, expedição — funções pagas como &ldquo;mais pesadas&rdquo;
        </li>
        <li>
          Mulher em função &ldquo;masculina&rdquo; geralmente recebe o salário maior
        </li>
      </UL>
      <Callout kind="warn">
        Antes de aceitar uma vaga, pergunte explicitamente quanto seu trabalho
        específico paga, não o valor &ldquo;da fábrica&rdquo;. As empreiteiras
        às vezes anunciam o valor mais alto da fábrica para atrair, e na
        chegada você descobre que sua função paga ¥200 a menos.
      </Callout>

      <H2 id="contrato-tipo">Tipos de contrato e o que muda</H2>

      <H3>Hakken (派遣) — empreiteira</H3>
      <P>
        99% dos brasileiros recém-chegados começam aqui. A empreiteira é seu
        empregador formal, e ela te aluga para a fábrica. Características:
      </P>
      <UL>
        <li>Pagamento por hora, valor entre ¥1.200 e ¥1.500</li>
        <li>Contrato curto (3 meses, 6 meses), renovável</li>
        <li>Sem bônus de verão/inverno na maioria dos casos</li>
        <li>Empreiteira frequentemente cuida do apartamento (e desconta caro)</li>
        <li>Demissão fácil — &ldquo;não vai chamar mais&rdquo;</li>
      </UL>

      <H3>Keiyaku shain (契約社員) — contrato direto temporário</H3>
      <P>
        A fábrica te contrata direto, mas com contrato anual. Salário um
        pouco maior, bônus pequeno, mais estabilidade.
      </P>
      <UL>
        <li>Pagamento por hora ou mensal fixo</li>
        <li>Contrato anual</li>
        <li>Bônus geralmente 1 mês de salário por ano</li>
      </UL>

      <H3>Seishain (正社員) — funcionário efetivo</H3>
      <P>
        Equivalente ao CLT brasileiro. Salário fixo mensal, bônus de verão e
        inverno (3 a 6 salários por ano em alguns casos), aposentadoria
        completa, vale-transporte, plano de saúde da empresa.
      </P>
      <UL>
        <li>Mensal fixo: ¥220.000–320.000 sem bônus</li>
        <li>Com bônus: equivalente mensal ¥280.000–450.000</li>
        <li>Plano de carreira, aumentos anuais</li>
        <li>Demissão difícil legalmente</li>
      </UL>
      <P>
        Conseguir virar seishain numa fábrica japonesa sendo brasileiro é
        possível mas leva tempo. Veja o guia separado sobre{" "}
        <Link to="/guias/shakai-vs-kokumin" className="text-accent underline">
          shakai hoken vs kokumin hoken
        </Link>{" "}
        para entender as implicações.
      </P>

      <H2 id="quanto-sobra">Quanto realmente sobra no fim do mês</H2>
      <P>
        Salário bruto é só o começo. Subtrair gastos típicos de quem mora no
        Japão:
      </P>
      <UL>
        <li>
          <Strong>Apato:</Strong> ¥35.000 a ¥70.000/mês (sozinho), ¥50.000–90.000 (casal/família)
        </li>
        <li>
          <Strong>Comida:</Strong> ¥30.000 a ¥50.000/mês (sozinho com refeição na fábrica)
        </li>
        <li>
          <Strong>Luz/gás/água:</Strong> ¥15.000–25.000/mês
        </li>
        <li>
          <Strong>Celular + internet:</Strong> ¥6.000–12.000/mês
        </li>
        <li>
          <Strong>Transporte:</Strong> ¥0 a ¥15.000 (se não vai a pé/bicicleta)
        </li>
      </UL>
      <P>
        Cenário realista para um solteiro ganhando ¥280.000 líquido:
      </P>
      <UL>
        <li>Gastos: ¥120.000</li>
        <li>Sobra: <Strong>¥160.000/mês</Strong> — possível guardar quase tudo se for econômico</li>
      </UL>
      <P>
        Cenário realista para um casal com 1 filho ganhando ¥460.000
        líquidos somados:
      </P>
      <UL>
        <li>Gastos: ¥230.000</li>
        <li>Sobra: <Strong>¥230.000/mês</Strong> com disciplina</li>
      </UL>

      <H2 id="armadilhas">Armadilhas que reduzem o salário real</H2>
      <UL>
        <li>
          <Strong>Desconto do apato pela empreiteira:</Strong> várias cobram
          ¥40.000–60.000 por um quarto compartilhado que vale ¥25.000 no
          mercado. Você não tem opção de procurar outro nas primeiras semanas.
        </li>
        <li>
          <Strong>Multa por sair antes do contrato:</Strong> algumas
          empreiteiras tentam descontar ¥50.000 a ¥150.000 se você sair antes
          do prazo. <Em>É ilegal</Em> na maioria dos casos — consulte o
          sindicato de trabalhadores estrangeiros.
        </li>
        <li>
          <Strong>Pausa não-paga maior que o legal:</Strong> empresas tentam
          tirar 1h30 de almoço (não paga) em vez de 1h. Verifique seu contrato.
        </li>
        <li>
          <Strong>Bônus &ldquo;de assiduidade&rdquo; sumido:</Strong> alguns
          contratos prometem ¥10.000–20.000/mês se você não faltar — e somem
          do hokokusho com qualquer falta, até de saúde.
        </li>
        <li>
          <Strong>Pagamento de viagem em dia de feriado:</Strong> dia parado
          sem culpa sua (acidente, falta de material na fábrica) deve ser pago
          como dia normal pela lei (休業手当 / kyugyo teate, 60% mínimo). Muita
          fábrica simplesmente não paga, e brasileiro não reclama.
        </li>
      </UL>

      <H2 id="conclusao">Conclusão</H2>
      <P>
        Em 2026, um operário brasileiro de fábrica no Japão ganha entre
        ¥250.000 e ¥320.000 bruto mensal com hora extra moderada, o que
        traduz em <Strong>¥200.000 a ¥260.000 líquidos</Strong>. Isso permite
        guardar ¥100.000 a ¥200.000/mês com disciplina.
      </P>
      <P>
        É um salário que muda vida no Brasil. É também um salário que
        muitos brasileiros gastam todo no Japão e voltam de mãos vazias.
        A diferença não é o salário — é a disciplina financeira.
      </P>
      <P>
        Para calcular exatamente quanto você ganharia ou ganha,{" "}
        <Link to="/calculadoras/salario" className="text-accent underline">
          use a calculadora
        </Link>
        . Em 30 segundos você tem o número líquido real para o seu caso.
      </P>
    </GuideLayout>
  );
}
